import { CSSProperties, MouseEvent, KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';
import {
  CheckboxIcon,
  Icon,
  IconName,
  SpecialAssetTypeWithIcon,
  getColorForAssetType,
} from '@ardoq/icons';
import { Selected } from 'aqTypes';
import { times } from 'lodash';
import { colors, s16, spacing } from '@ardoq/design-tokens';
import { AssetType } from '@ardoq/api-types';
import { RowType } from '@ardoq/table-legacy';
import { Text } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { WithPopoverPlacement, TextOverflow } from '@ardoq/popovers';
import { Button, UnstyledButton } from '@ardoq/button';
import { dishonestCurrentUser$ as currentUser$ } from 'streams/currentUser/currentUser$';
import { connect, derivedStream, dispatchAction } from '@ardoq/rxbeach';
import { SurveyRow } from 'components/AssetsBrowser/types';
import { useEffectOnce } from '@ardoq/hooks';
import {
  issueSurveyAccessRequest,
  issueSurveyWorkspaceAccessRequest,
  resetSurveyAccessRequestState,
  resetSurveyWorkspaceAccessRequestState,
} from './streams/actions';
import { entityBrowserAtoms$ } from './streams/entityBrowserAtoms$';
import { map } from 'rxjs';
import { focusOutline } from '@ardoq/style-helpers';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import { FlexBox } from '@ardoq/layout';
import { isDevelopmentMode } from 'env';
import { t, Trans, type I18nLocale } from '@ardoq/i18n';

const CheckboxIconStyled = styled(CheckboxIcon)`
  vertical-align: middle;
  margin-right: ${spacing.s4};
  cursor: pointer;
  &:focus {
    ${focusOutline}
  }
`;

type CheckboxProps = {
  style?: CSSProperties;
  onClick: (e: MouseEvent<any> | KeyboardEvent) => void;
  disabled?: boolean;
  isSelected: Selected;
};
export const Checkbox = ({ isSelected, onClick, ...props }: CheckboxProps) => (
  <CheckboxIconStyled
    role="checkbox"
    aria-checked={isSelected === Selected.SELECTED}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(e);
      }
    }}
    tabIndex={0}
    checked={isSelected === Selected.SELECTED}
    onClick={onClick}
    mixed={isSelected === Selected.MIXED ? true : undefined}
    {...props}
  />
);

const FolderIconStyled = styled(Icon)`
  color: ${colors.grey700};
  fill: ${colors.grey700};
  cursor: pointer;
  min-width: 24px;

  &:hover {
    color: ${colors.grey900};
    fill: ${colors.grey900};
  }
`;

type FolderIconProps = {
  isOpened?: boolean;
  onClick?: () => void;
};
export const FolderIcon = ({ isOpened, ...props }: FolderIconProps) => (
  <FolderIconStyled
    iconName={isOpened ? IconName.OPENED_FOLDER : IconName.FOLDER}
    {...props}
  />
);

export const RowIconWrapper = styled.span<{ $isDisabled?: boolean }>`
  margin-right: ${spacing.s4};
  margin-left: ${spacing.s4};
  display: flex;
  align-items: center;
  opacity: ${({ $isDisabled = false }) => ($isDisabled ? 0.5 : null)};
`;

const BaseRowIconStyles = styled(Icon)`
  color: ${colors.grey700};
  fill: ${colors.grey700};
`;

const rowTypeToColor: Record<RowType, string> = {
  [RowType.WORKSPACE]: getColorForAssetType(AssetType.WORKSPACE),
  [RowType.MANAGED_WORKSPACE]: getColorForAssetType(
    SpecialAssetTypeWithIcon.MANAGED_WORKSPACE
  ),
  [RowType.TRAVERSAL]: getColorForAssetType(AssetType.TRAVERSAL),
  [RowType.BOOKMARK]: getColorForAssetType(AssetType.BOOKMARK),
  [RowType.PRESENTATION]: getColorForAssetType(AssetType.PRESENTATION),
  [RowType.METAMODEL]: getColorForAssetType(AssetType.METAMODEL),
  [RowType.SURVEY]: getColorForAssetType(AssetType.SURVEY),
  [RowType.SCENARIO]: getColorForAssetType(AssetType.SCENARIO),
  [RowType.REPORT]: getColorForAssetType(AssetType.REPORT),
  [RowType.DASHBOARD]: getColorForAssetType(AssetType.DASHBOARD),
  [RowType.VIEWPOINT]: getColorForAssetType(AssetType.VIEWPOINT),
  [RowType.BROADCAST]: getColorForAssetType(AssetType.BROADCAST),
  // DocumentArchive
  [RowType.IMAGE]: getColorForAssetType(SpecialAssetTypeWithIcon.IMAGE),
  [RowType.DOCUMENT]: getColorForAssetType(SpecialAssetTypeWithIcon.DOCUMENT),
  [RowType.UNKOWN_FILE]: getColorForAssetType(
    SpecialAssetTypeWithIcon.UNKNOWN_FILE
  ),
  // not really needed, but we want to make TS happy
  [RowType.FOLDER]: getColorForAssetType(AssetType.FOLDER),
  [RowType.EMPTY_FOLDER]: getColorForAssetType(
    SpecialAssetTypeWithIcon.FOLDER_OPENED
  ),
  [RowType.COMPONENT_TYPE]: '#000',
};

const colorIcon = (assetType: RowType) => css`
  fill: ${rowTypeToColor[assetType]};
  color: ${rowTypeToColor[assetType]};
`;

export const WorkspaceIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.WORKSPACE,
})`
  ${colorIcon(RowType.WORKSPACE)}
  opacity: 0.6;
`;

export const ManagedWorkspaceIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.MANAGED_WORKSPACE,
})`
  ${colorIcon(RowType.MANAGED_WORKSPACE)};
  opacity: 0.6;
`;

export const PresentationIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.PRESENTATION,
})`
  ${colorIcon(RowType.PRESENTATION)}
  opacity: 0.6;
`;

export const MetamodelIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.METAMODEL,
})`
  ${colorIcon(RowType.METAMODEL)}
  opacity: 0.6;
`;

export const SurveyIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.SURVEYS,
})`
  ${colorIcon(RowType.SURVEY)}
  opacity: 0.6;
`;

export const ScenarioIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.SCENARIO,
})`
  ${colorIcon(RowType.SCENARIO)}
  opacity: 0.6;
`;

export const ReportIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.DESCRIPTION,
})`
  ${colorIcon(RowType.REPORT)}
  opacity: 0.6;
`;

export const DashboardIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.DASHBOARD,
})`
  ${colorIcon(RowType.DASHBOARD)}
  opacity: 0.6;
`;

export const UnknownFileIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.MISC,
})`
  ${colorIcon(RowType.UNKOWN_FILE)}
`;

export const TraversalIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.VIEWPOINT,
})`
  ${colorIcon(RowType.TRAVERSAL)}
`;

export const ViewpointIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.VIEWPOINT,
})`
  ${colorIcon(RowType.VIEWPOINT)}
`;

export const BroadcastIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.BROADCAST,
})`
  ${colorIcon(RowType.BROADCAST)}
`;

export const BookmarkIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.BOOKMARK,
})`
  ${colorIcon(RowType.BOOKMARK)}
`;

export const DocumentIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.SURVEYS,
})`
  ${colorIcon(RowType.DOCUMENT)}
`;

export const ImageIcon = styled(BaseRowIconStyles).attrs({
  iconName: IconName.COLLECTIONS,
})`
  ${colorIcon(RowType.IMAGE)}
`;

export const ClickableName = styled(UnstyledButton)<{
  onClick?: () => void;
  $isClickDisabled: boolean;
}>`
  flex: 1 1 100%;
  width: 100%;
  min-width: 200px;

  ${props =>
    props.onClick &&
    css`
      cursor: pointer;

      &:hover {
        color: ${colors.grey900};
        text-decoration: underline;

        svg {
          fill: ${colors.grey900};
        }
      }
    `}
  color: ${({ $isClickDisabled }) =>
    $isClickDisabled ? colors.grey500 : undefined}
`;

export const AqInlineEditInputWrapper = styled.span`
  width: 100%;
  position: relative;
  top: 2px;
`;

export const AqInlineEditInput = styled.input`
  color: ${colors.grey700};
  box-shadow: ${colors.grey50} 0px 1px 0px 0px;
  caret-color: ${colors.blue600};
  background-color: transparent;
  border: 0;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 1.5em;
  outline: none;
  transition: all 200ms ease-in-out 0s;

  &:focus {
    box-shadow: ${colors.blue600} 0px 2px 0px 0px;
  }
`;

export const TextEllipsisWrapper = styled.div<{ $width?: string }>`
  width: ${props => props.$width};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

export const TextEllipsisWrapperWithPopover = styled(TextOverflow)<{
  $width: string;
}>`
  overflow-wrap: anywhere;
  width: ${props => props.$width};
`;

export const NameCellWrapper = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;
  overflow: hidden;

  & > span {
    align-items: center;
    display: flex;
  }
`;

const Push = styled.div`
  display: inline-block;
  width: 39px;
  min-width: 39px;
`;

export const SortWrapper = styled(UnstyledButton)`
  display: flex;
  cursor: pointer;
  align-items: center;

  i {
    color: inherit;
    vertical-align: middle;
    margin-bottom: 4px;
  }

  &:hover {
    color: #000;
  }
`;

type IndentationProps = { level: number };
export const Indentation = ({ level }: IndentationProps) => (
  <>
    {times(level, i => (
      <Push key={i} />
    ))}
  </>
);

export const NameWrapper = styled.div<{ $indentationLevel: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(
    100% - 80px - 40px * ${props => props.$indentationLevel}
  ); /* space taken by checkbox and asset icon and indentation */
  position: relative; /* container for LinkOverAssetName */
`;

export const LinkOverAssetName = styled.div`
  position: absolute;
  right: 0;
  background-color: ${colors.grey50};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SearchPhrase = styled.span`
  font-weight: bold;
  text-decoration: underline;
  color: ${colors.grey950};
`;

const DisabledSurveyPopoverContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${s16};
  max-width: 270px;
`;

const getAnswerSurveyHref = (surveyId: string): string =>
  isDevelopmentMode()
    ? `http://${window.location.hostname}:4100/surveys/survey/${surveyId}`
    : `/surveys/survey/${surveyId}`;

const answerSurveyLinkComponent = (surveyId: string | undefined) =>
  surveyId ? (
    <Link
      typography="text2"
      hideIcon
      href={getAnswerSurveyHref(surveyId)}
      target="_blank"
      rel="noopener noreferrer"
    />
  ) : (
    <></>
  );

type DisabledSurveyReason =
  | 'insufficientSurveyPermission'
  | 'insufficientWorkspaceAdminPermission';
const DisabledSurveyPopoverContent = ({
  reason,
  survey,
  name,
  email,
  surveyWorkspaceAccessRequestSent,
  surveyWorkspaceAccessRequestLoading,
  surveyAccessRequestSent,
  surveyAccessRequestLoading,
  i18nLocale,
}: {
  reason: DisabledSurveyReason;
  surveyWorkspaceAccessRequestSent: boolean;
  surveyWorkspaceAccessRequestLoading: boolean;
  surveyAccessRequestSent: boolean;
  surveyAccessRequestLoading: boolean;
  survey?: SurveyRow;
  name?: string | null;
  email?: string | null;
  i18nLocale: I18nLocale;
}) => {
  useEffectOnce(() => {
    dispatchAction(resetSurveyWorkspaceAccessRequestState());
    dispatchAction(resetSurveyAccessRequestState());
  });

  const onRequestWorkspaceAccess = () => {
    dispatchAction(
      issueSurveyWorkspaceAccessRequest({
        survey,
        user: { name, email },
      })
    );
  };

  const onRequestSurveyAccess = () => {
    dispatchAction(
      issueSurveyAccessRequest({
        survey,
        user: { name, email },
      })
    );
  };

  if (reason === 'insufficientSurveyPermission') {
    return (
      <DisabledSurveyPopoverContentContainer>
        <Text>
          <Trans
            i18nKey="entityBrowser:disabledSurveyPopover.insufficientSurveyPermission"
            defaults="You can <answerLink>answer this Survey</answerLink> but you don't have writing permission to edit it"
            components={{
              answerLink: answerSurveyLinkComponent(survey?._id),
            }}
          />
        </Text>
        <FlexBox justify="end">
          <Button
            isLoading={surveyAccessRequestLoading}
            onClick={onRequestSurveyAccess}
            isDisabled={surveyAccessRequestLoading || surveyAccessRequestSent}
          >
            {!surveyAccessRequestSent
              ? t(
                  i18nLocale,
                  'entityBrowser:disabledSurveyPopover.requestWritingPermission',
                  'Request writing permission'
                )
              : t(
                  i18nLocale,
                  'entityBrowser:disabledSurveyPopover.requestSent',
                  'Request sent'
                )}
          </Button>
        </FlexBox>
      </DisabledSurveyPopoverContentContainer>
    );
  }

  if (reason === 'insufficientWorkspaceAdminPermission') {
    return (
      <DisabledSurveyPopoverContentContainer>
        <Text>
          <Trans
            i18nKey="entityBrowser:disabledSurveyPopover.insufficientWorkspaceAdminPermission"
            defaults="You can <answerLink>answer this Survey</answerLink> but you can't edit it because you don't have access to the selected workspace for the survey"
            components={{
              answerLink: answerSurveyLinkComponent(survey?._id),
            }}
          />
        </Text>
        <FlexBox justify="end">
          <Button
            isLoading={surveyWorkspaceAccessRequestLoading}
            onClick={onRequestWorkspaceAccess}
            isDisabled={
              surveyWorkspaceAccessRequestLoading ||
              surveyWorkspaceAccessRequestSent
            }
          >
            {!surveyWorkspaceAccessRequestSent
              ? t(
                  i18nLocale,
                  'entityBrowser:disabledSurveyPopover.requestWorkspaceAccess',
                  'Request workspace access'
                )
              : t(
                  i18nLocale,
                  'entityBrowser:disabledSurveyPopover.requestSent',
                  'Request sent'
                )}
          </Button>
        </FlexBox>
      </DisabledSurveyPopoverContentContainer>
    );
  }

  exhaustiveCheck(reason);
};

const ConnectedDisabledSurveyPopoverContent = connect(
  DisabledSurveyPopoverContent,
  derivedStream(
    'disabledSurveyPopoverContent$',
    entityBrowserAtoms$,
    currentUser$
  ).pipe(
    map(
      ([
        {
          surveyWorkspaceAccessRequestSent,
          surveyWorkspaceAccessRequestLoading,
          surveyAccessRequestSent,
          surveyAccessRequestLoading,
        },
        { name, email },
      ]) => ({
        surveyWorkspaceAccessRequestSent,
        surveyWorkspaceAccessRequestLoading,
        surveyAccessRequestSent,
        surveyAccessRequestLoading,
        name,
        email,
      })
    )
  )
);

export const getPopoverConfig = ({
  showPopover = false,
  reason,
  survey,
  i18nLocale,
}: {
  showPopover?: boolean;
  reason: DisabledSurveyReason;
  survey?: SurveyRow;
  i18nLocale: I18nLocale;
}) => ({
  preferredPlacement: WithPopoverPlacement.TOP,
  content: showPopover
    ? () => (
        <ConnectedDisabledSurveyPopoverContent
          reason={reason}
          survey={survey}
          i18nLocale={i18nLocale}
        />
      )
    : undefined,
});
